// Casca visual dos nós do canvas (mockup "Canvas Flow A"): cartão 12px de
// raio com cabeçalho (tile 24px com ícone no tom do grupo + título + menu
// "…" com Duplicar/Excluir), corpo 11.5px e alças redondas de 11px (entrada
// cinza à esquerda, saída ciano à direita). Cada nó só descreve o conteúdo.
import React, { memo, useState } from "react";
import { Handle } from "react-flow-renderer";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import {
  PlayArrow,
  ChatBubbleOutline,
  DynamicFeed,
  Layers,
  ImageOutlined,
  MicNone,
  VideocamOutlined,
  AltRoute,
  Shuffle,
  TimerOutlined,
  KeyboardOutlined,
  DataObject,
  SwapHoriz,
  ConfirmationNumberOutlined,
  SupportAgent,
  SellOutlined,
  LabelOffOutlined,
  Http,
  AllInclusive,
  AutoAwesome,
  MoreHoriz,
  ContentCopy,
  DeleteOutline,
  Add,
  WavingHand,
  StarRate,
  ErrorOutline,
} from "@mui/icons-material";
import { useNodeStorage } from "../../../stores/useNodeStorage";
import { NODE_META, TONES, nodeTitle } from "../flowRules";
import { i18n } from "../../../translate/i18n";

export const NODE_ICONS = {
  play: PlayArrow,
  chat: ChatBubbleOutline,
  menu: DynamicFeed,
  layers: Layers,
  image: ImageOutlined,
  mic: MicNone,
  video: VideocamOutlined,
  altRoute: AltRoute,
  shuffle: Shuffle,
  timer: TimerOutlined,
  keyboard: KeyboardOutlined,
  dataObject: DataObject,
  swap: SwapHoriz,
  ticket: ConfirmationNumberOutlined,
  agent: SupportAgent,
  sell: SellOutlined,
  labelOff: LabelOffOutlined,
  http: Http,
  openai: AllInclusive,
  gemini: AutoAwesome,
  add: Add,
  wave: WavingHand,
  star: StarRate,
};

export const NodeIcon = ({ name, size = 14, color }) => {
  const Icon = NODE_ICONS[name] || ChatBubbleOutline;
  return <Icon style={{ fontSize: size, color }} />;
};

// Tile colorido (usado no cabeçalho do nó, na paleta e no painel lateral).
export const NodeTile = ({ type, size = 24, iconSize = 14, radius = 7, tone, icon }) => {
  const meta = NODE_META[type] || {};
  const t = TONES[tone || meta.tone || "msg"];
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        background: t.bg,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flex: "none",
      }}
    >
      <NodeIcon name={icon || meta.icon} size={iconSize} color={t.color} />
    </div>
  );
};

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  return {
    card: {
      position: "relative",
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)"}`,
      borderRadius: 12,
      boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
      fontFamily: "Inter, sans-serif",
      color: theme.palette.text.primary,
      transition: "border-color .15s, box-shadow .15s",
    },
    selected: {
      borderColor: "#22D3EE",
      boxShadow: "0 0 0 3px rgba(34,211,238,0.18), 0 12px 32px rgba(0,0,0,0.5)",
    },
    error: {
      borderColor: "rgba(248,113,113,0.5)",
      boxShadow: "0 0 0 3px rgba(248,113,113,0.12)",
    },
    head: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "9px 12px",
      borderBottom: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.06)"}`,
    },
    title: { fontSize: 12, fontWeight: 700, flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
    more: {
      fontSize: 15,
      color: theme.palette.aeros.textTertiary,
      cursor: "pointer",
      flex: "none",
      "&:hover": { color: theme.palette.text.primary },
    },
    body: {
      padding: "10px 12px",
      fontSize: 11.5,
      color: theme.palette.text.secondary,
      lineHeight: 1.5,
      wordBreak: "break-word",
    },
    bodyError: { color: "#F87171" },
    // linha de opção (menu, randomizador, condição) — alça sai à direita
    option: {
      position: "relative",
      display: "flex",
      alignItems: "center",
      gap: 8,
      backgroundColor: theme.palette.aeros.sidebar,
      border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.07)"}`,
      borderRadius: 8,
      padding: "6px 10px",
      fontSize: 11.5,
      color: theme.palette.text.primary,
      minWidth: 0,
    },
    optionBadge: {
      width: 18,
      height: 18,
      borderRadius: 5,
      fontSize: 10,
      fontWeight: 800,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flex: "none",
    },
    optionLabel: { flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
    options: { padding: "0 12px 10px", display: "flex", flexDirection: "column", gap: 6 },
    menuItem: { fontSize: 13, gap: 8, "& .MuiListItemIcon-root": { minWidth: 0 }, "& .MuiSvgIcon-root": { fontSize: 16 } },
    danger: { color: "#F87171" },
  };
});

const HANDLE = {
  width: 11,
  height: 11,
  borderRadius: "50%",
  border: "2px solid #0B0D14",
  minWidth: 0,
  minHeight: 0,
};

export const targetHandleStyle = (top = 32) => ({ ...HANDLE, background: "#9CA3B8", left: -6, top });
export const sourceHandleStyle = (top = 32, color = "#22D3EE") => ({ ...HANDLE, background: color, right: -6, top });

// Alça de saída presa a uma linha de opção (fica fora do card, right:-18).
export const OptionHandle = ({ id, isConnectable, color = "#22D3EE" }) => (
  <Handle
    type="source"
    position="right"
    id={id}
    isConnectable={isConnectable}
    style={{ ...HANDLE, background: color, right: -18, top: "50%", transform: "translateY(-50%)" }}
  />
);

// Linha de opção com badge numerado/colorido e alça de saída própria.
export const OptionRow = ({ badge, label, tone = "msg", handleId, isConnectable, dotColor }) => {
  const classes = useStyles();
  const t = TONES[tone];
  return (
    <div className={classes.option}>
      <span className={classes.optionBadge} style={{ background: t.bg, color: t.color }}>
        {badge}
      </span>
      <span className={classes.optionLabel}>{label}</span>
      {handleId && <OptionHandle id={handleId} isConnectable={isConnectable} color={dotColor} />}
    </div>
  );
};

export const OptionList = ({ children }) => {
  const classes = useStyles();
  return <div className={classes.options}>{children}</div>;
};

const AerosNode = ({
  id,
  type,
  selected,
  isConnectable = true,
  width = 210,
  title,
  icon,
  tone,
  hasTarget = true,
  hasSource = true, // alça "a" padrão à direita do cabeçalho
  sourceColor,
  error, // string → borda vermelha + corpo vermelho
  canDuplicate = true,
  canDelete = true,
  children, // corpo (texto) ou elementos próprios
  options, // <OptionList> renderizado abaixo do corpo
  extra, // conteúdo livre (inputs) abaixo do corpo
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const storage = useNodeStorage();
  const [anchor, setAnchor] = useState(null);
  const label = title || nodeTitle({ type });
  const act = (action) => {
    setAnchor(null);
    storage.setNodesStorage(id);
    storage.setAct(action);
  };
  const showMenu = canDuplicate || canDelete;
  return (
    <div className={`${classes.card} ${selected ? classes.selected : ""} ${error ? classes.error : ""}`} style={{ width }}>
      {hasTarget && <Handle type="target" position="left" isConnectable={isConnectable} style={targetHandleStyle()} />}
      <div className={classes.head}>
        <NodeTile type={type} tone={tone} icon={icon} />
        <div className={classes.title}>{label}</div>
        {error ? (
          <ErrorOutline style={{ fontSize: 15, color: "#F87171" }} />
        ) : (
          showMenu && (
            <MoreHoriz
              className={`${classes.more} nodrag`}
              onClick={(e) => {
                e.stopPropagation();
                setAnchor(e.currentTarget);
              }}
              aria-label={i18n.t("aeros.flow.node.actions")}
            />
          )
        )}
      </div>
      {(children || error) && (
        <div className={`${classes.body} ${error ? classes.bodyError : ""}`}>{error || children}</div>
      )}
      {options}
      {extra}
      {hasSource && (
        <Handle
          type="source"
          position="right"
          id="a"
          isConnectable={isConnectable}
          style={sourceHandleStyle(32, sourceColor || (theme.palette.type === "light" ? theme.palette.primary.main : "#22D3EE"))}
        />
      )}
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)} onClick={(e) => e.stopPropagation()}>
        {canDuplicate && (
          <MenuItem className={classes.menuItem} onClick={() => act("duplicate")}>
            <ListItemIcon><ContentCopy /></ListItemIcon>
            {i18n.t("aeros.flow.node.duplicate")}
          </MenuItem>
        )}
        {canDelete && (
          <MenuItem className={`${classes.menuItem} ${classes.danger}`} onClick={() => act("delete")}>
            <ListItemIcon><DeleteOutline className={classes.danger} /></ListItemIcon>
            {i18n.t("aeros.flow.node.delete")}
          </MenuItem>
        )}
      </Menu>
    </div>
  );
};

export default memo(AerosNode);
